import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AppBar, Toolbar, Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, CssBaseline, IconButton, Box, Typography } from '@mui/material';
import { Settings, Assessment, MailOutline, ManageAccounts, Lock as LockIcon, Logout, Menu as MenuIcon, GitHub, Email, Notifications, Storage } from '@mui/icons-material';
import DebugInfo from '../components/DebugInfo';

const drawerWidth = 240;

export default function AdminLayout() {
    const auth = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [mobileOpen, setMobileOpen] = useState(false);
    const userRole = auth.user?.role;

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const handleLogout = () => {
        auth.logout();
        navigate('/admin/login');
    };

    const handleNavigation = (path) => {
        navigate(path);
        setMobileOpen(false);
    };

    const drawer = (
        <div>
            <Toolbar />
            <List>
                {userRole === 'ADMIN' && (
                    <ListItem disablePadding>
                        <ListItemButton onClick={() => handleNavigation('/admin/sentences')} selected={location.pathname.includes('/admin/sentences')}>
                            <ListItemIcon><Settings /></ListItemIcon>
                            <ListItemText primary="Gerenciar Sentenças" />
                        </ListItemButton>
                    </ListItem>
                )}
                {userRole === 'ADMIN' && (
                    <ListItem disablePadding>
                        <ListItemButton onClick={() => handleNavigation('/admin/sentence-files')} selected={location.pathname.includes('/admin/sentence-files')}>
                            <ListItemIcon><Storage /></ListItemIcon>
                            <ListItemText primary="Arquivos de Sentenças" />
                        </ListItemButton>
                    </ListItem>
                )}
                {userRole === 'ADMIN' && (
                    <ListItem disablePadding>
                        <ListItemButton onClick={() => handleNavigation('/admin/results')} selected={location.pathname.includes('/admin/results')}>
                            <ListItemIcon><Assessment /></ListItemIcon>
                            <ListItemText primary="Visualizar Resultados" />
                        </ListItemButton>
                    </ListItem>
                )}
                <ListItem disablePadding>
                    <ListItemButton onClick={() => handleNavigation('/admin/qrcode')} selected={location.pathname.includes('/admin/qrcode')}>
                        <ListItemIcon><MailOutline /></ListItemIcon>
                        <ListItemText primary="Envelope" />
                    </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                    <ListItemButton onClick={() => handleNavigation('/admin/participants')} selected={location.pathname.includes('/admin/participants')}>
                        <ListItemIcon><ManageAccounts /></ListItemIcon>
                        <ListItemText primary="Participantes" />
                    </ListItemButton>
                </ListItem>
                {userRole === 'ADMIN' && (
                    <ListItem disablePadding>
                        <ListItemButton onClick={() => handleNavigation('/admin/github')} selected={location.pathname.includes('/admin/github')}>
                            <ListItemIcon><GitHub /></ListItemIcon>
                            <ListItemText primary="GitHub" />
                        </ListItemButton>
                    </ListItem>
                )}
                {userRole === 'ADMIN' && (
                    <ListItem disablePadding>
                        <ListItemButton onClick={() => handleNavigation('/admin/email')} selected={location.pathname.includes('/admin/email')}>
                            <ListItemIcon><Email /></ListItemIcon>
                            <ListItemText primary="Configuração de Email" />
                        </ListItemButton>
                    </ListItem>
                )}
                {userRole === 'ADMIN' && (
                    <ListItem disablePadding>
                        <ListItemButton onClick={() => handleNavigation('/admin/notifications')} selected={location.pathname.includes('/admin/notifications')}>
                            <ListItemIcon><Notifications /></ListItemIcon>
                            <ListItemText primary="Notificações" />
                        </ListItemButton>
                    </ListItem>
                )}
                <ListItem disablePadding>
                    <ListItemButton onClick={() => handleNavigation('/admin/users')} selected={location.pathname.includes('/admin/users')}>
                        <ListItemIcon><LockIcon /></ListItemIcon>
                        <ListItemText primary={userRole === 'ADMIN' ? 'Gerenciar Usuários' : userRole === 'PARTICIPANTE' ? 'Alterar Senha' : 'Alterar Senha'} />
                    </ListItemButton>
                </ListItem>
            </List>
            <Box sx={{ position: 'absolute', bottom: 0, width: '100%' }}>
                 <List>
                    <ListItem disablePadding>
                        <ListItemButton onClick={handleLogout}>
                            <ListItemIcon><Logout /></ListItemIcon>
                            <ListItemText primary="Sair" />
                        </ListItemButton>
                    </ListItem>
                 </List>
            </Box>
        </div>
    );

    // Check if running in QA environment
    const isQA = process.env.PUBLIC_URL === '/qa';

    return (
        <Box sx={{ display: 'flex' }}>
            <CssBaseline />
            <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
                <Toolbar>
                    <IconButton color="inherit" aria-label="open drawer" edge="start" onClick={handleDrawerToggle} sx={{ mr: 2, display: { sm: 'none' } }}>
                        <MenuIcon />
                    </IconButton>
                    <Typography variant="h6" noWrap component="div">
                        Painel Administrativo
                    </Typography>
                    {isQA && (
                        <Box sx={{ ml: 'auto' }}>
                            <Typography variant="body2" sx={{
                                bgcolor: 'warning.main',
                                color: 'warning.contrastText',
                                px: 2,
                                py: 0.5,
                                borderRadius: 1,
                                fontWeight: 'bold'
                            }}>
                                QA
                            </Typography>
                        </Box>
                    )}
                </Toolbar>
            </AppBar>
            <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }} aria-label="admin sections">
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{ keepMounted: true }}
                    sx={{ display: { xs: 'block', sm: 'none' }, '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth } }}
                >
                    {drawer}
                </Drawer>
                <Drawer
                    variant="permanent"
                    sx={{ display: { xs: 'none', sm: 'block' }, '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth } }}
                    open
                >
                    {drawer}
                </Drawer>
            </Box>
            <Box component="main" sx={{ flexGrow: 1, p: 3, width: { sm: `calc(100% - ${drawerWidth}px)` } }}>
                <Toolbar />
                <Outlet />
            </Box>
            <DebugInfo />
        </Box>
    );
}
